import { useState, useCallback } from 'react';
import { useAuth } from '@providers';

/**
 * Action types for login prompts
 *
 * AIDEV-NOTE: Each type should have specific UI in LoginPromptModal.
 * Types are categorized by intent level for analytics:
 *
 * **Highest Intent (question interactions):**
 * - i_can_help: User wants to help someone
 * - me_too: User relates to a question
 * - upvote: User supports a question
 *
 * **High Intent (content/event actions):**
 * - event_checkin: User at a physical event
 * - create_event: User wants to host an event
 * - save_event: User wants to save an event
 * - going_event: User wants to RSVP (mark going) to an event
 * - view_my_events: Guest opening the personal Saved/Going event filters
 * - view_private_question: User trying to access gated content
 *
 * **Medium Intent (navigation actions):**
 * - access_profile: User exploring their account
 * - connect: User wants to network
 * - access_messages: User wants to see messages
 *
 * **Fallback:**
 * - generic: Default for unspecified actions
 *
 * NOTE: create_question is handled separately via email verification flow,
 * not through the login prompt modal.
 */
export type LoginPromptActionType =
  // Question interactions (highest intent)
  | 'i_can_help'
  | 'me_too'
  | 'upvote'
  // Event actions (high intent)
  | 'event_checkin'
  | 'create_event'
  | 'save_event'
  | 'going_event'
  | 'view_my_events'
  // Community actions (high intent)
  | 'create_community'
  | 'join_community'
  // Content access (high intent)
  | 'view_private_question'
  // Navigation actions (medium intent)
  | 'access_profile'
  | 'connect'
  | 'access_messages'
  // Fallback
  | 'generic';

/**
 * Hook to manage login prompt state and actions
 *
 * AIDEV-NOTE: Centralized hook for login prompt behavior.
 * Used by layout components to provide consistent login prompts
 * when guests attempt protected actions.
 *
 * @example
 * const { showPrompt, actionType, openPrompt, closePrompt, handleLogin } = useLoginPrompt();
 *
 * // In a nav item click handler:
 * if (requiresAuth && !isAuthenticated) {
 *   openPrompt('i_can_help');
 *   return;
 * }
 */
export const useLoginPrompt = () => {
  const { signIn } = useAuth();
  const [showPrompt, setShowPrompt] = useState(false);
  const [actionType, setActionType] = useState<LoginPromptActionType>('generic');

  const openPrompt = useCallback((type: LoginPromptActionType) => {
    setActionType(type);
    setShowPrompt(true);
  }, []);

  const closePrompt = useCallback(() => {
    setShowPrompt(false);
    setActionType('generic');
  }, []);

  const handleLogin = useCallback(() => {
    signIn({ actionType });
  }, [signIn, actionType]);

  return {
    showPrompt,
    actionType,
    openPrompt,
    closePrompt,
    handleLogin,
  };
};
